import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileText, Plus, Search, ChevronRight, ShieldCheck, PencilLine } from 'lucide-react';

interface Cover {
  id: string;
  cover_number: string;
  insured_name?: string;
  client_id?: string;
  client_name?: string;
  site_id?: string;
  site_name?: string;
  site_reference?: string;
  broker_id?: string;
  broker_name?: string;
  status: 'draft' | 'issued';
  policy_period_start?: string;
  policy_period_end?: string;
  totals?: { total_sum_insured_zar?: number; total_premium_zar?: number };
  created_at?: string;
  issued_at?: string;
}

const fmtZAR = (n?: number | null): string => {
  if (n == null || Number.isNaN(n)) return '—';
  return `R\u00a0${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export const CoversPage: React.FC = () => {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [covers, setCovers] = useState<Cover[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | 'draft' | 'issued'>('');
  const [loading, setLoading] = useState(true);

  const clientFilter = params.get('client_id') || '';
  const siteFilter = params.get('site_id') || '';

  const load = async () => {
    setLoading(true);
    try {
      const filters: Record<string, string | number> = { limit: 10000 };
      if (q) filters.q = q;
      if (status) filters.status = status;
      if (clientFilter) filters.client_id = clientFilter;
      if (siteFilter) filters.site_id = siteFilter;
      const r = await api.get<Cover[]>('/covers', { params: filters });
      setCovers(r.data);
    } catch (err) {
      toast.error('Failed to load covers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [status, clientFilter, siteFilter]);
  useEffect(() => { const t = setTimeout(() => void load(), 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const kpis = useMemo(() => {
    const total = covers.length;
    const issued = covers.filter((c) => c.status === 'issued').length;
    const draft = covers.filter((c) => c.status === 'draft').length;
    const totalSI = covers.reduce((s, c) => s + (c.totals?.total_sum_insured_zar || 0), 0);
    const totalPrem = covers.reduce((s, c) => s + (c.totals?.total_premium_zar || 0), 0);
    return { total, issued, draft, totalSI, totalPrem };
  }, [covers]);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="covers-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Confirmations of Cover</h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-2xl">
            Underwriting artefacts issued to brokers &amp; insureds. Each cover binds a client and site to a set of peril sections
            with sums insured, deductibles and premium.
          </p>
        </div>
        <Button variant="gradient" onClick={() => nav('/insurer/covers/new')} data-testid="new-cover-btn">
          <Plus size={16} className="mr-1" /> New confirmation of cover
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total" value={kpis.total.toString()} icon={<FileText size={16} />} />
        <KpiCard label="Issued" value={kpis.issued.toString()} tone="success" />
        <KpiCard label="Draft" value={kpis.draft.toString()} tone="warning" />
        <KpiCard label="Total sum insured" value={fmtZAR(kpis.totalSI)} tone="navy" />
        <KpiCard label="Total premium" value={fmtZAR(kpis.totalPrem)} tone="blue" />
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="font-headline">Confirmations ({covers.length})</CardTitle>
            <CardDescription>Ordered newest first.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={status} onChange={(e) => setStatus((e.target.value as '' | 'draft' | 'issued'))} className="h-8 w-36" data-testid="covers-status-filter">
              <option value="">All statuses</option>
              <option value="draft">Draft only</option>
              <option value="issued">Issued only</option>
            </Select>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cover № or insured…" className="pl-7 h-8 w-64" data-testid="covers-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-raptor-navy/50 font-body">Loading…</div>
          ) : covers.length === 0 ? (
            <div className="p-10 text-center text-raptor-navy/50 font-body">
              <FileText size={40} className="mx-auto mb-2 opacity-40" />
              No confirmations of cover yet. Click <b>New confirmation of cover</b> to create the first one.
            </div>
          ) : (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-left text-raptor-navy/70">
                <tr>
                  <th className="py-2 px-3">Cover №</th>
                  <th className="py-2 px-3">Insured</th>
                  <th className="py-2 px-3">Site</th>
                  <th className="py-2 px-3">Period</th>
                  <th className="py-2 px-3 text-right">Sum insured</th>
                  <th className="py-2 px-3 text-right">Premium</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {covers.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-border/60 hover:bg-raptor-neutral/30 cursor-pointer"
                    onClick={() => nav(`/insurer/covers/${c.id}`)}
                    data-testid={`cover-row-${c.id}`}
                  >
                    <td className="py-2 px-3 font-tabular font-semibold text-raptor-navy">{c.cover_number}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium text-raptor-navy">{c.insured_name || '—'}</div>
                      {c.client_name && <div className="text-xs text-raptor-navy/50">{c.client_name}</div>}
                    </td>
                    <td className="py-2 px-3 text-raptor-navy/70 text-xs">
                      {c.site_name || '—'}
                      {c.site_reference && <div className="font-tabular text-raptor-navy/50">{c.site_reference}</div>}
                    </td>
                    <td className="py-2 px-3 text-xs text-raptor-navy/70">
                      {fmtDate(c.policy_period_start)} → {fmtDate(c.policy_period_end)}
                    </td>
                    <td className="py-2 px-3 text-right font-tabular">{fmtZAR(c.totals?.total_sum_insured_zar)}</td>
                    <td className="py-2 px-3 text-right font-tabular">{fmtZAR(c.totals?.total_premium_zar)}</td>
                    <td className="py-2 px-3">
                      {c.status === 'issued' ? (
                        <Badge variant="success"><ShieldCheck size={10} /> Issued</Badge>
                      ) : (
                        <Badge variant="warning"><PencilLine size={10} /> Draft</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right"><ChevronRight size={14} className="text-raptor-navy/40 inline" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: string; icon?: React.ReactNode; tone?: 'navy' | 'blue' | 'success' | 'warning' }> = ({ label, value, tone }) => {
  const toneClasses: Record<string, string> = {
    navy: 'text-raptor-navy',
    blue: 'text-raptor-blue',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
  };
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-raptor-navy/60 font-body">{label}</div>
        <div className={`font-headline text-2xl tracking-wide ${tone ? toneClasses[tone] : 'text-raptor-navy'}`}>{value}</div>
      </CardContent>
    </Card>
  );
};

export default CoversPage;
